import { kadhavuExpUrl } from 'endpoints';
import { endLoading, setAlertMessage, setError, startLoading } from './app-dux';
import { login, setInteraction } from './interaction-dux';
import { getString } from '@nackle/intl-tools';
import { fetch } from '@nackle/umbrella';
import { PASSWORD_REPEAT_LIMIT } from '../constants';

/*******************************************************************************
 * Initial state
 */
const initialState = {
    token: '',
    tokenValid: false,
    passwordRules: {
        complexity: [],
        complexityRequirement: '',
        minLength: ''
    }
};

/*******************************************************************************
 * Non-async action types
 */
export const USER_ACTIVATION_STATUS = 'state/user_activation_status';
export const TOKEN_VALID = 'state/token_valid';
export const TOKEN_DATA = 'state/token_data';

/*******************************************************************************
 * Reducer
 */
export default function appReducer( state = initialState, action ) {
    switch ( action.type ) {
        case USER_ACTIVATION_STATUS: {
            return {
                ...state,
                activationRequest: action.activationRequest
            };
        }
        case TOKEN_DATA: {
            return {
                ...state,
                token: action.token,
                passwordRules: action.passwordRules
            };
        }
        case TOKEN_VALID: {
            return {
                ...state,
                tokenValid: action.valid
            };
        }
        default:
            return state;
    }
}

/*******************************************************************************
 * Action creators
 */
export function sendActivationEmail( username ) {

    return ( dispatch, getState ) => {
        const state = getState();
        const id = state.interaction && state.interaction.id ? state.interaction.id : sessionStorage.getItem( 'interactionId' );
        dispatch( startLoading() );
        return fetch( `${ kadhavuExpUrl }/sendActivationNotification`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(
                {
                    interactionId: id,
                    username: username,
                    locale: state.app.userSelectedLocale || ''
                }
            )
        } )
        .then( ( res ) => {
            if ( res.ok ) {
                return res.json().then( ( data ) => {
                    if ( data && !data.errors && data.success ) {
                        delete data.success;
                        if ( data.isActivated === false && data.toAddress.length > 0 ) {
                            const email = data.toAddress;
                            const successMessage = getString( 'activation.alert.emailSuccessMessage', { email: email } );
                            dispatch( setAlertMessage( successMessage, 'success' ) );
                        }
                        dispatch( userActivationStatus( data ) );
                        dispatch( endLoading() );
                    } else if ( data.errors ) {
                        const errors = [];
                        data.errors.forEach( error => {
                            if ( error.code === 'kadhavu.error.invalidActivationMethod' ) {
                                errors.push( { code: 'activation.error.invalidActivationMethod' } );
                            } else if ( error.code === 'kadhavu.error.activatedAccount' ) {
                                errors.push( { code: 'activation.error.activatedAccount' } );
                            } else if ( error.code === 'kadhavu.error.invalidEntry' ) {
                                errors.push( { code: 'activation.error.invalidEntry' } );
                            } else if ( error.code === 'kadhavu.error.nonuniqueEmailAddress' ) {
                                errors.push( { code: 'activation.error.nonuniqueEmailAddress' } );
                            } else {
                                errors.push( { code: 'activation.error.unknownError' } );
                            }
                        } );

                        dispatch( setError( errors ) );
                        dispatch( endLoading() );
                    }
                    dispatch( endLoading() );
                } );
            } else {
                return res.json().then( ( data ) => {
                    dispatch( endLoading() );
                    dispatch( setError( [ { code: 'activation.error.unknownError' } ] ) );
                } );
            }
        } )
        .catch ( ( err ) => {
            dispatch( endLoading() );
            dispatch( setError( [ { code: 'activation.error.unknownError' } ] ) );
            throw new Error( err );
        } );
    };
}

export function verifyToken( token, source ) {
    return ( dispatch )=> {
        dispatch( startLoading() );
        return fetch( `${ kadhavuExpUrl }/getPerson`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(
                {
                    token: token,
                    properites: { 
                        passwordRules: true,
                        interactionId: true,
                        isActivated: true,
                        distinctAddresses: true,
                        isTokenExpired: true,
                        isTokenRedeemed: true
                    }
                }
            )
        } ) .then( ( res ) => {
            if ( res.ok ) {
                return res.json().then( ( data ) => {
                    if ( data && !data.errors && data.success ) {
                            delete data.success;
                            dispatch( tokenData( data, token ) );
                        if ( data.isTokenExpired === false && data.isTokenRedeemed === false ) {

                            if ( ( data.isActivated === false && source === 'activation' ) || ( data.isActivated === true && source === 'resetPassword' ) ) {

                                dispatch( tokenValid( true ) );
                                dispatch( setInteraction( data.interactionId ) );
                                //restore the userSession storage before clearing it off
                                const savedSessionLocale = sessionStorage.getItem( 'userSessionLocale' );
                                //set interaction id in session storage to prevent refreshing from breaking login.
                                sessionStorage.clear();
                                sessionStorage.setItem( 'interactionId', data.interactionId );
                                sessionStorage.setItem( 'passwordRules', JSON.stringify( data.passwordRules ) );
                                sessionStorage.setItem( 'token', token );
                                sessionStorage.setItem( 'userSessionLocale', savedSessionLocale );
                                dispatch( endLoading() );

                            } else if ( data.isActivated === true && source === 'activation' ) {
                                dispatch( tokenValid( false ) );
                                dispatch( setError( [ { code: 'activation.error.activatedAccount' } ] ) );
                                dispatch( endLoading() );

                            } else {
                                dispatch( tokenValid( false ) );
                                dispatch( setError( [ { code: 'activation.error.invalidAccount' } ] ) );
                                dispatch( endLoading() );

                            }

                        } else if ( data.isActivated === true && source === 'activation' ) {
                            dispatch( tokenValid( false ) );
                            dispatch( setError( [ { code: 'activation.error.activatedAccount' } ] ) );
                            dispatch( endLoading() );

                        } else {
                            dispatch( tokenValid( false ) );
                            const errorMsgCode = source === 'activation' ?
                                'activation.error.invalidLink'
                                : source === 'resetPassword' ?
                                    'setPassword.error.invalidLink'
                                    : `${ source }.error.unknownError`;

                            dispatch( setError( [ { code: errorMsgCode } ] ) );
                            dispatch( endLoading() );

                        }

                    } else if ( data.errors ) {
                        const errors = [];
                        data.errors.forEach( error => {
                            if ( error.code === 'kadhavu.error.genericTwo' ) {
                                errors.push( { code: `${ source }.error.genericTwo` } );
                            } else if ( error.code === 'kadhavu.error.invalidEntry' ) {
                                errors.push( { code: `${ source }.error.invalidEntry` } );
                            } else {
                                errors.push( { code: `${ source }.error.unknownError` } );
                            }
                        } );

                        dispatch( setError( errors ) );

                        dispatch( endLoading() );
                    }

                    dispatch( endLoading() );

                } );
            } else {
                return res.json().then( ( data ) => {
                    dispatch( endLoading() );
                    dispatch( setError( [ { code: 'common.error.unknownError' } ] ) );
                } );
            }
        } )
        .catch ( ( err ) => {
            dispatch( endLoading() );
            dispatch( setError( [ { code: 'common.error.unknownError' } ] ) );
            throw new Error( err );
        } );
    };
}
export function setPassword( token, password, companyId, idUrl ) {
    return ( dispatch, getState ) => {
        const state = getState();
        dispatch( startLoading() );
        const code = token ? token : sessionStorage.getItem( 'token' );
        if ( code ) {
            return fetch( `${ kadhavuExpUrl }/setPassword`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify(
                    {
                        token: code, 
                        password: password, 
                        locale: state.app.userSelectedLocale || ''
                    }
                )
            } ) .then( ( res ) => {
                if ( res.ok ) {
                    return res.json().then( async ( data ) => {
                        if ( data && !data.errors ) {
                            delete data.success;
                            await dispatch( login( data.username, password, companyId, idUrl ) );
                        } else if ( data.errors ) {
                            const errors = [];
                            data.errors.forEach( error => {
                                if ( error.code === 'kadhavu.error.invalidAccount' ) {
                                    errors.push( { code: 'setPassword.error.invalidAccount' } );
                                } else if ( error.code === 'kadhavu.error.invalidLink' ) {
                                    errors.push( { code: 'setPassword.error.invalidLink' } );
                                } else if ( error.code === 'kadhavu.error.invalidToken' ) {
                                    errors.push( { code: 'setPassword.error.invalidToken' } );
                                } else if ( error.code === 'kadhavu.error.terminatedWithNoPoints' ) {
                                    errors.push( { code: 'kadhavu.error.terminatedWithNoPoints' } );
                                } else if ( error.code === 'kadhavu.error.passwordRepeatLimit' ) {
                                    errors.push( { code: 'setPassword.error.passwordRepeatLimit', data: { passwordRepeatLimit: PASSWORD_REPEAT_LIMIT } } );
                                } else {
                                    errors.push( { code: 'setPassword.error.unknownError' } );
                                }
                            } );

                            dispatch( setError( errors ) );
                            dispatch( endLoading() );
                        }
                        dispatch( endLoading() );
                    } );
                } else {
                    return res.json().then( ( data ) => {
                        dispatch( setError( [ { code: 'setPassword.error.unknownError' } ] ) );
                        dispatch( endLoading() );
                    } );
                }
            } )
            .catch ( ( err ) => {
                dispatch( setError( [ { code: 'setPassword.error.unknownError' } ] ) );
                dispatch( endLoading() );
            } );
        } else {
            dispatch( setError( [ { code: 'setPassword.error.unknownError' } ] ) );
            dispatch( endLoading() );
        }
        dispatch( endLoading() );
        return false;
    };
}
function tokenValid( valid ) {
    return {
        type: TOKEN_VALID,
        valid

    };
}

function userActivationStatus( data ) {
    return {
        type: USER_ACTIVATION_STATUS,
        activationRequest: data
    };
}

function tokenData( data, token ) {
    return {
        type: TOKEN_DATA,
        data,
        token
    };
}